import {
  ErrorSchema,
  ErrorSchemaBuilder,
  FieldErrors,
  RJSFSchema,
  StrictRJSFSchema,
  UiSchema,
} from '@rjsf/utils';
import produce from 'immer';

import { filterUsableObjects, UsableObjectConfig } from 'dashboard/lib/workflows';
import {
  IdentifierActionMeta,
  StatusActionMeta,
  WorkflowActionMeta,
  WorkflowConfigError,
} from 'dashboard/models/workflow';
import {
  AllowedActionMetaType,
  AllowedObjectType,
  GenericWorkflowAction,
  GenericWorkflowActionEnumType,
  UiSchemaWidgetTypes,
} from 'dashboard/models/workflow-schema';
import { WorkflowActionFromConfig } from 'dashboard/models/workflow-version';
import { JsonSchemaTypes } from 'lib/json-schema';

// https://github.com/rjsf-team/react-jsonschema-form/issues/3829
// The validator errors when validating a large number of oneOf options,
// so we need to use enum and enumNames instead.
const transformOneOfOptions = (
  options: { title: string; const: string }[],
):
  | {
      oneOf: {
        title: string;
        const: string;
      }[];
    }
  | { enum: string[]; enumNames: string[] } => {
  if (options.length > 100) {
    return {
      enum: options.map((option) => option.const),
      enumNames: options.map((option) => option.title),
    };
  } else {
    return { oneOf: options };
  }
};

const metaSelectSchemaAdditions = (
  enumTypeOptions: AllowedActionMetaType[],
  usableObjects: UsableObjectConfig[],
  dependentOnValue: string | null,
  dependentOnType: string | null,
  actionMeta: WorkflowActionMeta,
): Partial<RJSFSchema> => {
  const actionMetaOptions = enumTypeOptions.flatMap((actionMetaType) => actionMeta[actionMetaType]);
  if (!actionMetaOptions.length) {
    return {};
  }

  if (dependentOnValue) {
    const templateFilterTokens = (
      usableObjects.find(({ value }) => value === dependentOnValue)?.filters?.[
        'template-filters'
      ] || []
    ).map(({ id }) => id);
    const actionMetaObjects = actionMetaOptions.filter(
      (actionMetaObject): actionMetaObject is StatusActionMeta | IdentifierActionMeta =>
        'templateToken' in actionMetaObject,
    );
    const oneOfOptions =
      templateFilterTokens.length > 0
        ? actionMetaObjects
            .filter((actionMetaObject) =>
              templateFilterTokens.includes(actionMetaObject.templateToken),
            )
            .map(({ name, token }) => ({
              title: name,
              const: token,
            }))
        : actionMetaObjects.map(({ displayName, token }) => ({
            title: displayName,
            const: token,
          }));
    if (oneOfOptions.length > 0) {
      return transformOneOfOptions(oneOfOptions);
    } else {
      return {};
    }
  }

  if (dependentOnType) {
    const actionMetaObjects = actionMetaOptions.filter(
      (actionMetaObject): actionMetaObject is StatusActionMeta | IdentifierActionMeta =>
        'templateToken' in actionMetaObject,
    );
    const oneOfOptions =
      dependentOnType != null
        ? actionMetaObjects
            .filter((actionMetaObject) => actionMetaObject.templateToken === dependentOnType)
            .map(({ displayName, token }) => ({
              title: displayName,
              const: token,
            }))
        : [];

    if (oneOfOptions.length > 0) {
      return transformOneOfOptions(oneOfOptions);
    } else {
      return {};
    }
  }

  return transformOneOfOptions(
    actionMetaOptions.map(({ name, token }) => ({
      title: name,
      const: token,
    })),
  );
};

const objectSelectSchemaAdditions = (usableObjects: UsableObjectConfig[]): Partial<RJSFSchema> => {
  if (!usableObjects.length) {
    return {};
  } else {
    return {
      oneOf: usableObjects.map((usableObject) => ({
        title: usableObject.label,
        const: usableObject.value,
      })),
      default: usableObjects[0].value,
    };
  }
};

const combinePropertySchemaWithAdditions = (
  propertySchema: RJSFSchema,
  schemaAddition: Partial<RJSFSchema>,
): RJSFSchema => {
  if (propertySchema.type === JsonSchemaTypes.Array) {
    return produce(propertySchema, (combinedSchema) => {
      combinedSchema.uniqueItems = true;
      const itemSchema = (propertySchema.items as RJSFSchema | null) || {};
      const combinedItemProperties = produce(itemSchema, (combinedItemSchema) => {
        // (evan.qi) we use enum and enumNames for anyOf schemas with
        // a large number of options
        if (schemaAddition.enum) {
          combinedItemSchema.enum = [
            ...(itemSchema.oneOf || [])
              .filter((option): option is StrictRJSFSchema => typeof option !== 'boolean')
              .map((option) => option.const as string),
            ...(schemaAddition.enum || []),
          ];
          combinedItemSchema.enumNames = [
            ...(itemSchema.oneOf || [])
              .filter((option): option is StrictRJSFSchema => typeof option !== 'boolean')
              .map((option) => option.title),
            ...(schemaAddition.enumNames || []),
          ];
        } else if (itemSchema.oneOf || schemaAddition.oneOf) {
          combinedItemSchema.oneOf = [...(itemSchema.oneOf || []), ...(schemaAddition.oneOf || [])];
        }
        return combinedItemSchema;
      });
      combinedSchema.items = combinedItemProperties;

      return combinedSchema;
    });
  } else {
    return produce(propertySchema, (combinedSchema) => {
      const defaultValue = propertySchema?.default || schemaAddition.default;
      if (defaultValue) {
        combinedSchema.default = defaultValue;
      }
      if (schemaAddition.enum) {
        combinedSchema.enum = [
          ...(propertySchema.oneOf || [])
            .filter((option): option is StrictRJSFSchema => typeof option !== 'boolean')
            .map((option) => option.const as string),
          ...(schemaAddition.enum || []),
        ];
        combinedSchema.enumNames = [
          ...(propertySchema.oneOf || [])
            .filter((option): option is StrictRJSFSchema => typeof option !== 'boolean')
            .map((option) => option.title),
          ...(schemaAddition.enumNames || []),
        ];
      } else if (propertySchema.oneOf || schemaAddition.oneOf) {
        combinedSchema.oneOf = [...(propertySchema.oneOf || []), ...(schemaAddition.oneOf || [])];
      }

      return combinedSchema;
    });
  }
};

const errorsForPropertySchema = (
  propertySchema: RJSFSchema,
  uiSchema: UiSchema,
  required: boolean,
): FieldErrors => {
  // required select fields must have options
  if (
    uiSchema?.['ui:widget'] === UiSchemaWidgetTypes.Select &&
    required &&
    !(
      propertySchema.oneOf ||
      ((propertySchema.items as RJSFSchema | null) || {}).oneOf ||
      propertySchema.enum
    )
  ) {
    return { __errors: ['options must not be empty'] };
  }

  return {};
};

const schemaAndErrorsFor = ({
  propertySchema,
  uiSchema,
  usableObjects,
  actionParameters,
  actionMeta,
  required,
}: {
  propertySchema: RJSFSchema;
  uiSchema: UiSchema;
  usableObjects: UsableObjectConfig[];
  actionParameters: WorkflowActionFromConfig['params'];
  actionMeta: WorkflowActionMeta;
  required: boolean;
}): { schema: RJSFSchema; fieldErrors: FieldErrors } => {
  const uiOptions = uiSchema?.['ui:options'] ?? {};
  if ('enumType' in uiOptions && 'enumTypeOptions' in uiOptions) {
    const schemaAddition = produce({}, (schema) => {
      switch (uiOptions.enumType) {
        case GenericWorkflowActionEnumType.Object: {
          return objectSelectSchemaAdditions(
            filterUsableObjects({
              usableObjects,
              options: {
                permittedObjects: uiOptions.enumTypeOptions as AllowedObjectType[],
                excludeNonPersonaObjects: true,
              },
            }),
          );
        }
        case GenericWorkflowActionEnumType.Meta: {
          return metaSelectSchemaAdditions(
            uiOptions.enumTypeOptions as AllowedActionMetaType[],
            filterUsableObjects({
              usableObjects,
              options: {
                excludeNonPersonaObjects: true,
              },
            }),
            uiOptions.dependentOn
              ? (actionParameters[uiOptions.dependentOn as string] as string)
              : null,
            uiOptions.dependentOnType
              ? (actionParameters[uiOptions.dependentOnType as string] as string)
              : null,
            actionMeta,
          );
        }
      }
      return schema;
    });

    const finalSchema = combinePropertySchemaWithAdditions(propertySchema, schemaAddition);
    return {
      schema: finalSchema,
      fieldErrors: errorsForPropertySchema(finalSchema, uiSchema, required),
    };
  }

  return { schema: propertySchema, fieldErrors: {} };
};

export const generateRjsfSchemaAndErrors = ({
  schema,
  uiSchema,
  usableObjects,
  actionParameters,
  actionMeta,
}: {
  schema: GenericWorkflowAction['request']['schema'];
  uiSchema: GenericWorkflowAction['request']['uiSchema'];
  usableObjects: UsableObjectConfig[];
  actionParameters: WorkflowActionFromConfig['params'];
  actionMeta: WorkflowActionMeta;
}): { schema: RJSFSchema; errorSchema: ErrorSchema } => {
  const properties = schema.properties ?? {};
  const requiredProperties = schema.required ?? [];
  const errorSchemaBuilder = new ErrorSchemaBuilder();
  const generatedSchema = Object.keys(properties).reduce((acc, property) => {
    const { schema: propertySchema, fieldErrors: propertyFieldErrors } = schemaAndErrorsFor({
      propertySchema: properties[property] as RJSFSchema,
      uiSchema: uiSchema[property],
      usableObjects,
      actionParameters,
      actionMeta,
      required: requiredProperties.includes(property),
    });
    if (propertyFieldErrors.__errors) {
      errorSchemaBuilder.addErrors(
        propertyFieldErrors.__errors.map((error) =>
          [uiSchema[property]['ui:title'], error].join(' '),
        ),
        property,
      );
    }
    return {
      ...acc,
      [property]: propertySchema,
    };
  }, {});

  return {
    schema: {
      ...schema,
      properties: generatedSchema,
    },
    errorSchema: errorSchemaBuilder.ErrorSchema,
  };
};

export const filterErrorsForField = (
  errors: WorkflowConfigError[],
  fieldId: string,
): WorkflowConfigError[] => {
  const fieldKey = fieldId.split('__').pop();
  return errors.filter((error) => error.field === fieldKey);
};

export const transformErrorProperty = (property?: string): WorkflowConfigError['field'] => {
  if (property) {
    // (evan.qi) property sometimes comes as '.property' instead of 'property', so
    // first slice out the leading '.' and then reference root level property so
    // we can use ui:title to assemble full error message
    return (property.startsWith('.') ? property.slice(1) : property).split('.')[0];
  }
};
